import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { formatNumber, t } from 'ember-intl';
import { local } from 'embroider-css-modules';
import {
  COLOR_PARITY_LOSS,
  COLOR_PARITY_MEET,
  COLOR_PARITY_WIN,
} from 'frontend-utils/utils/css-variable';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import type {
  ParityInsightHistoricalData,
  ParityInsightSegmentationItem,
} from 'lumen/models';
import type {
  DateAggregation,
  ParityFilter,
  Percentages,
} from 'lumen/utils/constants/pi-constant-values';
import {
  BENCHMARK,
  DATE_AGGREGATION_WEEK,
  PARITY_FILTER,
} from 'lumen/utils/constants/pi-constant-values';
import {
  calculateBenchmarkPercentages,
  calculateParityAnalyticsValues,
} from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';

import styles from './chart-parity-score-gauge.module.css';

const BENCHMARK_INNER_RADIUS = '100%';
const BENCHMARK_OUTER_RADIUS = '110%';
const SCORE_INNER_RADIUS = '85%';
const SCORE_OUTER_RADIUS = '95%';

type ChartData = {
  color: string;
  id: string;
  innerRadius: string;
  name: string;
  radius: string;
  y: number; // percentage
}[];

interface ParityChartsChartParityScoreGaugeSignature {
  Args: {
    data?: ParityInsightSegmentationItem;
    dateAggregation: DateAggregation;
    extractDate: string;
    extractDateKey: string;
    hideHotelsMetric?: boolean;
    historicalChartData?: ParityInsightHistoricalData | null;
    isBenchmarkVisible?: boolean;
    isLoading: boolean;
    parityFilters: Set<ParityFilter>;
  };
}

export default class ParityChartsChartParityScoreGauge extends Component<ParityChartsChartParityScoreGaugeSignature> {
  @service declare intl: Services['intl'];

  get benchmarkPercentages(): Percentages {
    const { data } = this.args;

    if (data?.benchmark) {
      const percentages = calculateBenchmarkPercentages(data.benchmark);
      const { loss, meet, win } = roundParityPercentages(percentages);

      return { loss, meet, win };
    }

    return { loss: 0, meet: 0, win: 0 };
  }

  // Benchmark value based on the current loss/meet/win filter
  get benchmarkValue(): number | undefined {
    const { parityFilters } = this.args;

    let total = 0;

    parityFilters.forEach((parityFilter) => {
      total += this.benchmarkPercentages[parityFilter];
    });

    return total > 0 ? total : undefined;
  }

  // WoW or MoM difference
  get changeVersusLastPeriod(): number | undefined {
    if (this.parityScoreLastWeek) {
      return this.parityScore - this.parityScoreLastWeek;
    }

    return undefined;
  }

  get chartData(): { data: ChartData }[] {
    const { isBenchmarkVisible, parityFilters } = this.args;

    const isWinSelected = parityFilters.has(PARITY_FILTER.WIN);
    const isMeetSelected = parityFilters.has(PARITY_FILTER.MEET);
    const isLossSelected = parityFilters.has(PARITY_FILTER.LOSS);

    // eslint-disable-next-line prefer-const
    let { loss, meet, win } = this.percentages;

    // Values in the gauge need to include whatever is rendered before
    // loss = loss, meet = loss + meet, win = loss + meet + win
    if (isLossSelected) {
      meet += loss;
      win += loss;
    }

    if (isMeetSelected) {
      win += this.percentages.meet;
    }

    const parityData: ChartData = [];

    // Show the same data in the widget as in the evolution chart
    if (isWinSelected) {
      parityData.push({
        color: `var(${COLOR_PARITY_WIN})`,
        id: PARITY_FILTER.WIN,
        innerRadius: SCORE_INNER_RADIUS,
        name: this.intl.t('parity_analytics.win'),
        radius: SCORE_OUTER_RADIUS,
        y: win * 100,
      });
    }

    if (isMeetSelected) {
      parityData.push({
        color: `var(${COLOR_PARITY_MEET})`,
        id: PARITY_FILTER.MEET,
        innerRadius: SCORE_INNER_RADIUS,
        name: this.intl.t('parity_analytics.meet'),
        radius: SCORE_OUTER_RADIUS,
        y: meet * 100,
      });
    }

    if (isLossSelected) {
      parityData.push({
        color: `var(${COLOR_PARITY_LOSS})`,
        id: PARITY_FILTER.LOSS,
        innerRadius: SCORE_INNER_RADIUS,
        name: this.intl.t('parity_analytics.loss'),
        radius: SCORE_OUTER_RADIUS,
        y: loss * 100,
      });
    }

    const allFiltersSelected =
      isLossSelected && isMeetSelected && isWinSelected;

    if (!isBenchmarkVisible || allFiltersSelected) {
      return [{ data: parityData }];
    }

    const benchmarkData: ChartData = [
      {
        color: 'var(--prism-color-chart-accent-secondary-figure-default)',
        id: BENCHMARK,
        innerRadius: BENCHMARK_INNER_RADIUS,
        name: this.intl.t('demand.benchmark'),
        radius: BENCHMARK_OUTER_RADIUS,
        y: this.benchmarkValue !== undefined ? this.benchmarkValue * 100 : 0,
      },
    ];

    return [{ data: parityData }, { data: benchmarkData }];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        type: 'solidgauge',
        height: '50%',
      },

      tooltip: {
        backgroundColor: null,
        borderWidth: 0,
        className: 'tooltip-widget',
        shadow: false,
        useHTML: true,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        pointFormatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { id: metricKey } = this.options;

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const metricValue =
            metricKey === BENCHMARK
              ? that.benchmarkValue
              : // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                that.percentages[metricKey];

          // @ts-expect-error: Incorrect type
          const formattedMetricValue =
            metricValue !== null
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                that.intl.formatNumber(metricValue, { style: 'percent' })
              : that.intl.t('general.missing');

          // @ts-expect-error: Incorrect type
          return `${this.name}: ${formattedMetricValue}`;
        },
      },

      pane: {
        center: ['50%', '100%'],
        size: '180%',
        startAngle: -90,
        endAngle: 90,
        background: [
          {
            shape: 'arc',
            innerRadius: SCORE_INNER_RADIUS,
            outerRadius: SCORE_OUTER_RADIUS,
            backgroundColor: 'var(--prism-color-chart-neutral-figure-empty)',
            borderWidth: 0,
          },
        ],
      },

      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickPositions: [],
        className: 'gauge-label',
        title: {
          align: 'low',
          textAlign: 'center',
          text: this.intl.formatNumber(this.parityScore, { style: 'percent' }),
          style: { color: 'var(--prism-color-text-neutral-default)' },
        },
      },

      plotOptions: {
        solidgauge: {
          dataLabels: { enabled: false },
          rounded: true,
          tooltip: { followPointer: true },
        },
      },
    };
  }

  // Formatted WoW or MoM text
  get formattedChange(): string {
    const { changeVersusLastPeriod: change } = this;

    if (change === undefined) {
      return this.intl.t('general.missing');
    }

    const versusString =
      this.args.dateAggregation === DATE_AGGREGATION_WEEK
        ? this.intl.t('ranking.vs_last_week')
        : this.intl.t('ranking.vs_last_month');

    if (change === 0) {
      return `${this.intl.t('ranking.no_change')} ${versusString}`;
    }

    const changeInPercentage = this.intl.formatNumber(change, {
      style: 'percent',
    });

    return change > 0
      ? `+${changeInPercentage} ${versusString}`
      : `${changeInPercentage} ${versusString}`;
  }

  // Last week or month percentages
  get lastPeriodPercentages(): Percentages {
    const { extractDateKey, historicalChartData } = this.args;

    if (historicalChartData?.all) {
      const historicalChartDataKeys = Object.keys(historicalChartData.all);
      const index = historicalChartDataKeys.indexOf(extractDateKey);

      // Period at index 0 never has a predecessor
      if (index > 0) {
        const lastPeriodKey = historicalChartDataKeys[index - 1];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const lastPeriodData = historicalChartData.all[lastPeriodKey][0]?.value;

        if (lastPeriodData) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const percentages = calculateParityAnalyticsValues(lastPeriodData);
          const { loss, meet, win } = roundParityPercentages(percentages);

          return { loss, meet, win };
        }
      }
    }

    return { loss: 0, meet: 0, win: 0 };
  }

  get parityScore(): number {
    const { parityFilters } = this.args;

    let parityScore = 0;

    parityFilters.forEach((parityFilter) => {
      parityScore += this.percentages[parityFilter];
    });

    return parityScore;
  }

  get parityScoreLastWeek(): number {
    const { parityFilters } = this.args;

    let parityScoreLastWeek = 0;

    parityFilters.forEach((parityFilter) => {
      parityScoreLastWeek += this.lastPeriodPercentages[parityFilter];
    });

    return parityScoreLastWeek;
  }

  get percentages(): Percentages {
    const { data } = this.args;

    if (data) {
      const percentages = calculateParityAnalyticsValues(data.value);
      const { loss, meet, win } = roundParityPercentages(percentages);

      return { loss, meet, win };
    }

    return { loss: 0, meet: 0, win: 0 };
  }

  get totalNbOfHotels(): string {
    const { data } = this.args;

    if (data?.value?.subscriptions) {
      return this.intl.formatNumber(data.value.subscriptions);
    }

    return '-';
  }

  get totalNbOfShops(): string {
    const { data } = this.args;

    if (data) {
      const percentages = calculateParityAnalyticsValues(data.value);

      return this.intl.formatNumber(percentages.totalNbOfShops);
    }

    return '-';
  }

  // Construct the widget title based on the current loss/meet/win filters. e.g. "Meet + Win"
  get widgetTitle(): string {
    const { parityFilters } = this.args;

    return Array.from(parityFilters)
      .map((parityFilter) => this.intl.t(`parity_analytics.${parityFilter}`))
      .join(' + ');
  }

  <template>
    <PrismCard @isSecondary={{true}}>
      <:content>
        <div class="mt-300">
          <div class={{styles.gauge-chart}}>
            {{#unless @isLoading}}
              <GeneralChartsOtaChart
                {{! @glint-expect-error: Incorrect type }}
                @chartOptions={{this.chartOptions}}
                @model={{this.chartData}}
              />
            {{/unless}}
          </div>
          <div class={{styles.widget-row}}>
            <p class="text-300--regular truncate">
              {{this.widgetTitle}}
            </p>
          </div>

          <div class={{styles.widget-row}}>
            {{#if @isLoading}}
              <div class="{{styles.widget-row--loading}} w-full">
                <span class="loading-pulse w-1/2" />
              </div>
            {{else}}
              <h4 class="truncate">
                {{this.formattedChange}}
              </h4>
            {{/if}}
          </div>

          <div class={{local styles "widget-row" "widget-row--large"}}>
            {{#unless @hideHotelsMetric}}
              <div class="w-1/2 p-200">
                {{#if @isLoading}}
                  <div class={{styles.widget-row--loading}}>
                    <span class="loading-pulse w-1/2" />
                  </div>
                {{else}}
                  <h3 class="truncate">
                    {{this.totalNbOfHotels}}
                  </h3>
                {{/if}}
                <p class="text-200--regular truncate">
                  {{t "general.hotels"}}
                </p>
              </div>
            {{/unless}}
            <div class="w-1/2 p-200">
              {{#if @isLoading}}
                <div class={{styles.widget-row--loading}}>
                  <span class="loading-pulse w-1/2" />
                </div>
              {{else}}
                <h3 class="truncate">
                  {{this.totalNbOfShops}}
                </h3>
              {{/if}}
              <p
                class="text-200--regular flex items-center justify-center gap-100 truncate"
              >
                {{t "parity_analytics.number_of_shops"}}
                <GeneralIconWithTooltip
                  @side="top"
                  @tooltipContent={{t
                    "parity_analytics.number_of_shops_explanation"
                  }}
                />
              </p>
            </div>
          </div>

          <div class={{local styles "widget-row" "widget-row--large"}}>
            <div class="w-1/3 p-200">
              {{#if @isLoading}}
                <div class={{styles.widget-row--loading}}>
                  <span class="loading-pulse w-1/2" />
                </div>
              {{else}}
                <h3 class="truncate">
                  {{formatNumber this.percentages.loss style="percent"}}
                </h3>
              {{/if}}

              <p
                class="text-200--regular flex items-center justify-center gap-100 truncate"
              >
                {{t "parity_analytics.loss"}}
                <GeneralIconWithTooltip
                  @side="top"
                  @tooltipContent={{t "parity_analytics.loss_explanation"}}
                />
              </p>
            </div>

            <div class="w-1/3 p-200">
              {{#if @isLoading}}
                <div class={{styles.widget-row--loading}}>
                  <span class="loading-pulse w-1/2" />
                </div>
              {{else}}
                <h3 class="truncate">
                  {{formatNumber this.percentages.meet style="percent"}}
                </h3>
              {{/if}}

              <p
                class="text-200--regular flex items-center justify-center gap-100 truncate"
              >
                {{t "parity_analytics.meet"}}
                <GeneralIconWithTooltip
                  @side="top"
                  @tooltipContent={{t "parity_analytics.meet_explanation"}}
                />
              </p>
            </div>

            <div class="w-1/3 p-200">
              {{#if @isLoading}}
                <div class={{styles.widget-row--loading}}>
                  <span class="loading-pulse w-1/2" />
                </div>
              {{else}}
                <h3 class="truncate">
                  {{formatNumber this.percentages.win style="percent"}}
                </h3>
              {{/if}}

              <p
                class="text-200--regular flex items-center justify-center gap-100 truncate"
              >
                {{t "parity_analytics.win"}}
                <GeneralIconWithTooltip
                  @side="top"
                  @tooltipContent={{t "parity_analytics.win_explanation"}}
                />
              </p>
            </div>
          </div>
        </div>
      </:content>
    </PrismCard>
  </template>
}
